import { useTeamsData } from '@lib/hooks/useTeamsData';
import { getUserThemes } from '@services/themes';
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { type Theme } from '../store/ThemeStore';

type UserDetailsType = {
  default_theme_id: string,
  themes: Theme[]
}

export const GET_THEMES_KEY = 'get_themes';

export default function useFetchThemes({ enabled = true }: { enabled?: boolean } = {}) {
  const currentTeam = useTeamsData((s) => s.currentTeam);

  const darkThemeId = useRef(undefined);

  const userDetailsId = currentTeam?.billing_account?.owner?.user_details_id;

  const { data, error, isLoading, isSuccess, refetch } = useQuery({
    queryKey: [GET_THEMES_KEY, userDetailsId],
    queryFn: () => getUserThemes({ userDetailsId }),
    enabled: !!userDetailsId && enabled
  });

  let userDetails: UserDetailsType | undefined;
  let defaultUserDetails: UserDetailsType | undefined;

  if (data?.entity) {
    for (const uDetails of data.entity) {
      if (uDetails.id === userDetailsId) userDetails = uDetails;
      else {
        darkThemeId.current = uDetails.themes.find((theme: Theme) => theme.name === 'Dark' && !theme.config.default.imageSrc)?.id;
        defaultUserDetails = uDetails;
      }
    }
  }

  const defaultThemeId = userDetails?.default_theme_id || defaultUserDetails?.default_theme_id || darkThemeId.current;

  const getThemeSettings = (selectedThemeId?: string | null) => {
    if (!userDetails || !defaultUserDetails) {
      return {
        colors: undefined,
        settings: undefined
      };
    }
    const themeId = selectedThemeId || defaultThemeId || darkThemeId.current;
    const theme = [...userDetails.themes, ...defaultUserDetails.themes].find(theme => theme.id === themeId);
    if (!theme) {
      return {
        colors: undefined,
        settings: undefined
      };
    }
    return {
      default: theme.config.default,
      colors: theme.config.colors,
      settings: { ...theme.config.textFontFamily, ...theme.config.headingFontFamily, ...theme.config.images }
    };
  };

  return {
    userThemes: userDetails?.themes,
    defaultThemes: defaultUserDetails?.themes,
    defaultThemeId,
    darkThemeId: darkThemeId.current,
    error,
    isLoading,
    isSuccess,
    refetch,
    getThemeSettings
  };
}
